import { useState } from 'react';
import { motion } from 'framer-motion';

export default function QuizModule({ subject, curriculum = 'icse' }) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [quizStarted, setQuizStarted] = useState(false);

  // Sample quiz data - this would normally come from a database or API
  const quizData = {
    icse: {
      physics: [
        {
          question: "What is the SI unit of force?",
          options: ["Newton", "Joule", "Watt", "Pascal"],
          correct: 0,
          explanation: "Newton is the SI unit of force, named after Sir Isaac Newton."
        },
        {
          question: "Which law of motion states that every action has an equal and opposite reaction?",
          options: ["First Law", "Second Law", "Third Law", "Fourth Law"],
          correct: 2,
          explanation: "Newton's Third Law states that for every action, there is an equal and opposite reaction."
        },
        {
          question: "What is the formula for kinetic energy?",
          options: ["KE = mv", "KE = 1/2 mv²", "KE = mgh", "KE = Fd"],
          correct: 1,
          explanation: "Kinetic energy is calculated as KE = 1/2 mv², where m is mass and v is velocity."
        }
      ],
      chemistry: [
        {
          question: "What is the atomic number of Carbon?",
          options: ["4", "6", "8", "12"],
          correct: 1,
          explanation: "Carbon has an atomic number of 6, meaning it has 6 protons in its nucleus."
        },
        {
          question: "Which of the following is a noble gas?",
          options: ["Oxygen", "Nitrogen", "Helium", "Hydrogen"],
          correct: 2,
          explanation: "Helium is a noble gas, known for being chemically inert."
        }
      ],
      mathematics: [
        {
          question: "What is the value of π (pi) approximately?",
          options: ["3.14", "3.14159", "3.1416", "All of the above"],
          correct: 3,
          explanation: "π is approximately 3.14159, often rounded to 3.14 or 3.1416 depending on precision needed."
        }
      ]
    },
    isc: {
      physics: [
        {
          question: "What is the unit of electric charge?",
          options: ["Coulomb", "Volt", "Ampere", "Ohm"],
          correct: 0,
          explanation: "Coulomb is the SI unit of electric charge."
        },
        {
          question: "Which phenomenon demonstrates the wave nature of light?",
          options: ["Photoelectric effect", "Diffraction", "Compton effect", "Pair production"],
          correct: 1,
          explanation: "Diffraction is a wave phenomenon that demonstrates the wave nature of light."
        }
      ],
      chemistry: [
        {
          question: "What is the hybridization of carbon in methane (CH₄)?",
          options: ["sp", "sp²", "sp³", "sp³d"],
          correct: 2,
          explanation: "Carbon in methane undergoes sp³ hybridization, forming four equivalent C-H bonds."
        }
      ]
    }
  };

  const questions = quizData[curriculum]?.[subject?.toLowerCase()] || quizData[curriculum]?.physics || [];

  const handleAnswerSelect = (answerIndex) => {
    setSelectedAnswer(answerIndex);
  };

  const handleNextQuestion = () => {
    if (selectedAnswer === questions[currentQuestion].correct) {
      setScore(score + 1);
    }
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
    } else {
      setShowResult(true);
    }
  };

  const handleRestartQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setQuizStarted(false);
  };

  const handleStartQuiz = () => {
    setQuizStarted(true);
  };

  if (!quizStarted) {
    return (
      <div className="glass-panel rounded-2xl p-6 border border-white/10">
        <div className="text-center">
          <div className="text-5xl mb-4">🧠</div>
          <h3 className="text-2xl font-bold text-white mb-2">Practice Quiz</h3>
          <p className="text-gray-400 mb-4">
            Test your knowledge with {questions.length} questions on {subject || 'Physics'}
          </p>
          <button
            onClick={handleStartQuiz}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            Start Quiz
          </button>
        </div>
      </div>
    );
  }

  if (showResult) {
    const percentage = Math.round((score / questions.length) * 100);
    return (
      <div className="glass-panel rounded-2xl p-6 border border-white/10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="text-6xl mb-4">
            {percentage >= 70 ? '🎉' : percentage >= 50 ? '👍' : '📚'}
          </div>
          <h3 className="text-3xl font-bold text-white mb-2">Quiz Complete!</h3>
          <div className="text-5xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent mb-4">
            {percentage}%
          </div>
          <p className="text-gray-400 mb-6">
            You scored {score} out of {questions.length} questions
          </p>
          <button
            onClick={handleRestartQuiz}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            Try Again
          </button>
        </motion.div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];

  return (
    <div className="glass-panel rounded-2xl p-6 border border-white/10">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm text-gray-400">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <span className="text-sm text-purple-400">
            Score: {score}
          </span>
        </div>
        <div className="w-full bg-white/10 rounded-full h-2">
          <div
            className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      <motion.div
        key={currentQuestion}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="mb-6"
      >
        <h3 className="text-xl font-bold text-white mb-6">{currentQ.question}</h3>
        <div className="space-y-3">
          {currentQ.options.map((option, index) => (
            <button
              key={index}
              onClick={() => handleAnswerSelect(index)}
              className={`w-full p-4 rounded-lg text-left transition-all ${
                selectedAnswer === index
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              <span className="font-medium mr-3">{String.fromCharCode(65 + index)}.</span>
              {option}
            </button>
          ))}
        </div>
      </motion.div>

      {selectedAnswer !== null && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6"
        >
          <div className={`p-4 rounded-lg mb-4 ${
            selectedAnswer === currentQ.correct
              ? 'bg-green-500/20 border border-green-500/30'
              : 'bg-red-500/20 border border-red-500/30'
          }`}>
            <p className="text-white font-medium mb-2">
              {selectedAnswer === currentQ.correct ? '✓ Correct!' : '✗ Incorrect'}
            </p>
            <p className="text-gray-300 text-sm">{currentQ.explanation}</p>
          </div>
          <button
            onClick={handleNextQuestion}
            className="w-full py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            {currentQuestion < questions.length - 1 ? 'Next Question' : 'See Results'}
          </button>
        </motion.div>
      )}
    </div>
  );
}
